import type { FilterHierarchyConfig, FilterHierarchyLevel, FilterHierarchyNode } from '~/shared/utils/filterHierarchy';

export type CascadingFilterOption = { id: string; name: string; description?: string };

const normalizeCompanyToken = (value: string): string =>
  String(value)
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    // SARTable labels append "(MMP)" / mimicking — strip before comparing to filter options.
    .replace(/\b(mmp|mimicking)\b/g, ' ')
    .replace(/\b(inc|incorporated|corp|corporation|ltd|limited|plc|co|company|holdings|holding|group|the)\b/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const hasBoundedToken = (haystack: string, needle: string): boolean =>
  haystack === needle ||
  haystack.startsWith(`${needle} `) ||
  haystack.endsWith(` ${needle}`) ||
  haystack.includes(` ${needle} `);

const nodeEqualsTarget = (node: FilterHierarchyNode, target: string): boolean => {
  const needle = String(target).trim();
  if (!needle) return false;
  if (node.id === needle || node.name === needle) return true;

  const normalizedNeedle = normalizeCompanyToken(needle);
  if (!normalizedNeedle) return false;

  const normalizedId = normalizeCompanyToken(node.id);
  const normalizedName = normalizeCompanyToken(node.name);
  return normalizedId === normalizedNeedle || normalizedName === normalizedNeedle;
};

/** Loose leaf match: equality, or either token contains the other (ticker / short labels). */
const nodeFuzzyMatchesTarget = (node: FilterHierarchyNode, target: string): boolean => {
  if (nodeEqualsTarget(node, target)) return true;

  const normalizedNeedle = normalizeCompanyToken(target);
  if (normalizedNeedle.length < 2) return false;

  const candidates = [normalizeCompanyToken(node.id), normalizeCompanyToken(node.name)].filter(Boolean);
  return candidates.some(token => {
    if (token === normalizedNeedle) return true;
    // Allow short brands like "3m" / "hp" via bounded token match (length >= 2).
    if (normalizedNeedle.length >= 2 && hasBoundedToken(token, normalizedNeedle)) return true;
    if (token.length >= 2 && hasBoundedToken(normalizedNeedle, token)) return true;
    // Broader containment for longer labels (ticker / partial names).
    if (normalizedNeedle.length >= 3 && token.includes(normalizedNeedle)) return true;
    if (token.length >= 3 && normalizedNeedle.includes(token)) return true;
    return false;
  });
};

const findNodeById = (nodes: FilterHierarchyNode[], id: string): FilterHierarchyNode | null => {
  for (const node of nodes) {
    if (node.id === id) return node;
    if (node.options?.length) {
      const nested = findNodeById(node.options, id);
      if (nested) return nested;
    }
  }
  return null;
};

const pickPreferredPath = (matches: string[][], preferRootId?: string): string[] | null => {
  if (!matches.length) return null;
  // Prefer deepest (leaf) hits so matching a company beats matching a parent label.
  const maxLen = Math.max(...matches.map(path => path.length));
  const deepest = matches.filter(path => path.length === maxLen);
  if (preferRootId) {
    const preferred = deepest.find(path => path[0] === preferRootId);
    if (preferred) return preferred;
  }
  return deepest[0];
};

/** Ancestor ids from root → node (inclusive). Matches node id or display name (normalized/fuzzy). */
export function findPathIdsToNode(
  nodes: FilterHierarchyNode[],
  target: string,
  preferRootId?: string,
): string[] | null {
  const needle = String(target).trim();
  if (!needle) return null;

  const exactMatches: string[][] = [];
  const fuzzyMatches: string[][] = [];
  const walk = (list: FilterHierarchyNode[], prefix: string[]) => {
    for (const node of list) {
      const path = [...prefix, node.id];
      if (nodeEqualsTarget(node, needle)) {
        exactMatches.push(path);
      } else if (nodeFuzzyMatchesTarget(node, needle)) {
        fuzzyMatches.push(path);
      }
      if (node.options?.length) {
        walk(node.options, path);
      }
    }
  };
  walk(nodes, []);

  return pickPreferredPath(exactMatches, preferRootId) ?? pickPreferredPath(fuzzyMatches, preferRootId);
}

/** Options for level `levelIndex` given prior selections (by hierarchy id). */
export function getCascadingOptionsAtLevel(
  tree: FilterHierarchyConfig,
  selection: Record<string, string>,
  levelIndex: number,
): CascadingFilterOption[] {
  const level = tree.filterHierarchy[levelIndex];
  if (!level) return [];

  if (levelIndex === 0) {
    return tree.options.map(node => ({
      id: node.id,
      name: node.name,
      ...(node.description ? { description: node.description } : {}),
    }));
  }

  let nodes: FilterHierarchyNode[] = tree.options;
  for (let depth = 0; depth < levelIndex; depth += 1) {
    const levelId = tree.filterHierarchy[depth]?.id;
    const selectedId = levelId ? selection[levelId] : undefined;
    if (!selectedId) return [];
    const selectedNode = findNodeById(nodes, selectedId);
    if (!selectedNode?.options?.length) return [];
    nodes = selectedNode.options;
  }

  return nodes.map(node => ({
    id: node.id,
    name: node.name,
    ...(node.description ? { description: node.description } : {}),
  }));
}

export function findNodeLabel(
  tree: FilterHierarchyConfig,
  selection: Record<string, string>,
  levelId: string,
): string | null {
  const levelIndex = tree.filterHierarchy.findIndex(level => level.id === levelId);
  if (levelIndex < 0) return null;
  const selectedId = selection[levelId];
  if (!selectedId) return null;
  const options = getCascadingOptionsAtLevel(tree, selection, levelIndex);
  return options.find(option => option.id === selectedId)?.name ?? null;
}

/** Description for the selected node at `levelId`, if BE provided one on the filter option. */
export function findNodeDescription(
  tree: FilterHierarchyConfig,
  selection: Record<string, string>,
  levelId: string,
): string | null {
  const levelIndex = tree.filterHierarchy.findIndex(level => level.id === levelId);
  if (levelIndex < 0) return null;
  const selectedId = selection[levelId];
  if (!selectedId) return null;
  const options = getCascadingOptionsAtLevel(tree, selection, levelIndex);
  const description = options.find(option => option.id === selectedId)?.description;
  const trimmed = typeof description === 'string' ? description.trim() : '';
  return trimmed || null;
}

/** Defaults: optional preferred id per level, else the first valid option. */
export function buildSelectionFromDefaults(tree: FilterHierarchyConfig): Record<string, string> {
  const selection: Record<string, string> = {};
  tree.filterHierarchy.forEach((level, index) => {
    const options = getCascadingOptionsAtLevel(tree, selection, index);
    const preferred = tree.defaultSelection?.[level.id];
    const match = preferred ? options.find(option => option.id === preferred) : undefined;
    selection[level.id] = match?.id ?? options[0]?.id ?? '';
  });
  return selection;
}

export function buildSelectionFromPartial(
  tree: FilterHierarchyConfig,
  partial: Record<string, string | undefined>,
): Record<string, string> {
  let selection = buildSelectionFromDefaults(tree);

  // If a deeper level is set without its ancestors (e.g. company without company-type),
  // resolve the full path from the tree so the cascading bar can select it.
  // Always overwrite ancestors from the leaf path — keeping a stale company-type (e.g. private)
  // while selecting a public company causes widget-data 500s.
  // When a company exists under both private and public, prefer the root already in partial
  // (or the one just resolved for this click) so we don't flip Public → Private.
  const enrichedPartial = { ...partial };
  const rootLevelId = tree.filterHierarchy[0]?.id;
  for (let index = tree.filterHierarchy.length - 1; index >= 0; index -= 1) {
    const level = tree.filterHierarchy[index];
    const incoming = enrichedPartial[level.id];
    if (!incoming) continue;
    const preferRootId = rootLevelId ? enrichedPartial[rootLevelId] : undefined;
    const path = findPathIdsToNode(tree.options, incoming, preferRootId);
    if (!path || path.length !== index + 1) continue;
    path.forEach((id, pathIndex) => {
      const ancestorLevel = tree.filterHierarchy[pathIndex];
      if (ancestorLevel) {
        enrichedPartial[ancestorLevel.id] = id;
      }
    });
  }

  tree.filterHierarchy.forEach((level, index) => {
    const incoming = enrichedPartial[level.id];
    if (!incoming) return;

    const options = getCascadingOptionsAtLevel(tree, selection, index);
    const matched =
      options.find(option => option.id === incoming || option.name === incoming) ??
      options.find(option => nodeEqualsTarget(option as FilterHierarchyNode, incoming)) ??
      options.find(option => nodeFuzzyMatchesTarget(option as FilterHierarchyNode, incoming));
    if (!matched) return;

    selection = { ...selection, [level.id]: matched.id };
    for (let downstream = index + 1; downstream < tree.filterHierarchy.length; downstream += 1) {
      const downstreamLevel = tree.filterHierarchy[downstream];
      const downstreamOptions = getCascadingOptionsAtLevel(tree, selection, downstream);
      const preferred = enrichedPartial[downstreamLevel.id] ?? tree.defaultSelection?.[downstreamLevel.id];
      const resolved =
        (preferred
          ? downstreamOptions.find(option => option.id === preferred || option.name === preferred)?.id
          : undefined) ??
        downstreamOptions[0]?.id ??
        '';
      selection[downstreamLevel.id] = resolved;
    }
  });

  return selection;
}

/** Map a root→node path onto hierarchy level ids (aligned from the leaf). */
const pathToPartialSelection = (tree: FilterHierarchyConfig, path: string[]): Record<string, string> | null => {
  const levels = tree.filterHierarchy;
  if (!levels.length || !path.length) return null;

  const partial: Record<string, string> = {};
  if (path.length <= levels.length) {
    // Path is root→…→node; map from the top.
    path.forEach((id, index) => {
      partial[levels[index].id] = id;
    });
  } else {
    // Path deeper than declared hierarchy — keep the trailing segment.
    const offset = path.length - levels.length;
    levels.forEach((level, index) => {
      partial[level.id] = path[offset + index];
    });
  }
  return partial;
};

/** Resolve a click/token to a full cascading selection (or null if unknown). */
export function resolveCascadingSelectionFromCandidates(
  tree: FilterHierarchyConfig,
  candidates: Array<string | undefined | null>,
  preferRootId?: string,
): Record<string, string> | null {
  if (!tree?.filterHierarchy?.length) return null;

  const leafDepth = tree.filterHierarchy.length;

  for (const candidate of candidates) {
    if (candidate === undefined || candidate === null || candidate === '') continue;
    const path = findPathIdsToNode(tree.options, String(candidate), preferRootId);
    if (!path?.length) continue;

    // Prefer leaf (company) hits; skip bare root matches like "public"/"private".
    if (path.length < leafDepth && leafDepth > 1) continue;

    const partial = pathToPartialSelection(tree, path);
    if (!partial) continue;
    return buildSelectionFromPartial(tree, partial);
  }

  return null;
}

export function applyCascadingLevelChange(
  tree: FilterHierarchyConfig,
  current: Record<string, string>,
  levelId: string,
  nextId: string,
): Record<string, string> {
  const levelIndex = tree.filterHierarchy.findIndex(level => level.id === levelId);
  if (levelIndex < 0) return current;

  const next: Record<string, string> = { ...current, [levelId]: nextId };
  for (let downstream = levelIndex + 1; downstream < tree.filterHierarchy.length; downstream += 1) {
    const downstreamLevel = tree.filterHierarchy[downstream];
    const options = getCascadingOptionsAtLevel(tree, next, downstream);
    next[downstreamLevel.id] = options[0]?.id ?? '';
  }
  return next;
}

export function selectionToFilterParams(
  tree: FilterHierarchyConfig,
  selection: Record<string, string>,
): { ids: Record<string, string>; labels: Record<string, string> } {
  const ids: Record<string, string> = {};
  const labels: Record<string, string> = {};

  tree.filterHierarchy.forEach(level => {
    const id = selection[level.id];
    if (!id) return;
    ids[level.id] = id;
    const label = findNodeLabel(tree, selection, level.id);
    labels[level.name] = label ?? id;
    labels[level.id] = label ?? id;
  });

  return { ids, labels };
}

export function getCascadingHierarchyIds(tree: FilterHierarchyConfig): Set<string> {
  return new Set(tree.filterHierarchy.map((level: FilterHierarchyLevel) => level.id));
}
